<template>
    <div class="animated fadeIn">
        <div class="kgr-row">
            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top kgr-row">
                <div class="kgr-postfix__fluid">
                    <kgr-view-picker
                        :value="view"
                        @post-input="updateView"
                    />
                </div>
                <div class="kgr-postfix__fixed">
                    <a
                        v-if="isCreatable"
                        class="kgr-button kgr-button--accent"
                        :href="createMemberLink"
                    >
                        <kgr-icon
                            icon-class="add"
                            small
                            circle
                            highlight
                            class="kgr-button__icon kgr-button__icon--left"
                        />
                        <span>{{ $t('Neues Mitglied hinzufügen') }}</span>
                    </a>
                </div>
                <div class="kgr-postfix__fixed">
                    <kgr-print-button />
                </div>
            </div>
            <div class="kgr-grid kgr-grid--columnGutters kgr-grid--inlineBlock">
                <div class="kgr-grid__cell">
                    <kgr-member-status-picker
                        :statuses="statuses"
                        :types="types"
                        @post-input="updateStatuses"
                    />
                    <div
                        v-if="hasAccountingAccess"
                    >
                        <div class="kgr-inputGroup__label">
                            Abrechnungsstatus
                        </div>
                        <kgr-checkbox
                            v-model="filterDunningActive"
                            label="Rechnungssperre aktiv"
                            inline
                        />
                        <kgr-checkbox
                            v-model="filterDunningStopActive"
                            label="Mahnsperre aktiv"
                            inline
                        />
                    </div>
                </div>
            </div>
            <div class="kgr-grid kgr-grid--columnGutters kgr-grid--inlineBlock">
                <div
                    v-if="isShortProfileGloballyViewable"
                    class="kgr-grid__cell"
                >
                    <label class="kgr-inputGroup">
                        <div class="kgr-inputGroup__label">{{ $t('Abteilungen') }}</div>
                        <kgr-multi-select
                            v-model="selectedDepartments"
                            :options="filteredDepartments"
                            :loading="loading"
                            display="name"
                            searchable
                            @post-input="load()"
                        />
                    </label>
                </div>
            </div>
            <div class="kgr-grid kgr-grid--columnGutters kgr-grid--inlineBlock">
                <div
                    v-if="isShortProfileGloballyViewable"
                    class="kgr-grid__cell"
                >
                    <label class="kgr-inputGroup">
                        <div class="kgr-inputGroup__label">{{ $t('Teams') }}</div>
                        <kgr-multi-select
                            v-model="selectedTeams"
                            :options="filteredTeams"
                            :loading="loading"
                            display="extendedName"
                            searchable
                            @post-input="load()"
                        />
                    </label>
                </div>
            </div>
            <div class="kgr-grid kgr-grid--columnGutters kgr-row">
                <div class="kgr-grid__cell">
                    <label class="kgr-inputGroup">
                        <div class="kgr-inputGroup__label">{{ $t('search') }}</div>
                        <input
                            v-model="search"
                            type="text"
                            class="kgr-input"
                        />
                    </label>
                </div>
                <div class="kgr-grid__cell">
                    <label class="kgr-inputGroup">
                        <div class="kgr-inputGroup__label">{{ $t('sort') }}</div>
                        <kgr-sort-select
                            :value="sort"
                            @post-input="updateSort"
                        />
                    </label>
                </div>
                <div class="kgr-grid__cell">
                    <div class="kgr-inputGroup__label">
&nbsp;
                    </div>
                </div>
            </div>
        </div>
        <hr class="kgr-separator" />
        <div>
            <kgr-members-list
                :members="members"
                :view="view"
                :loading="loading"
                :can-load-more="canLoadMore"
                @loadMore="loadMore"
            />
        </div>
    </div>
</template>

<script>
import Checkbox from '../../components/common/form/checkbox/Checkbox.vue';
import ClubEmployeeDepartmentsAndTeamsRepository from '../../js/repository/ClubEmployeeDepartmentsAndTeamsRepository.js';
import Icon from '../../components/common/icon/Icon.vue';
import KgrMemberStatusPicker from '../../components/common/form/picker/MemberStatusPicker.vue';
import MemberRepository from '../../js/repository/MemberRepository.js';
import MembersList from '../../components/members/MembersList.vue';
import MultiSelect from '../../components/common/form/select/MultiSelect.vue';
import PrintButton from '../../components/common/form/button/PrintButton.vue';
import SortSelect from '../../components/members/SortSelect.vue';
import ViewPicker from '../../components/common/form/picker/ViewPicker.vue';

const MEMBERS_OVERVIEW_VIEW = 'kgr:members:overview:view';
const MEMBERS_OVERVIEW_SORT = 'kgr:members:overview:sort';
const MEMBERS_OVERVIEW_STATUSES = 'kgr:members:overview:contract-statuses';
const MEMBERS_OVERVIEW_TYPES = 'kgr:members:overview:contract-types';

export default {
    components: {
        'kgr-member-status-picker': KgrMemberStatusPicker,
        'kgr-multi-select': MultiSelect,
        'kgr-members-list': MembersList,
        'kgr-print-button': PrintButton,
        'kgr-sort-select': SortSelect,
        'kgr-view-picker': ViewPicker,
        'kgr-icon': Icon,
        'kgr-checkbox': Checkbox,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
        isShortProfileGloballyViewable: {
            type: Boolean,
            required: true,
        },
        hasAccountingAccess: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            search: '',
            sort: 'lastName',
            statuses: [0, 1, 2, 3],
            types: ['regular', 'passive', 'guest'],
            view: 'grid',
            useDepartmentsAndTeamFilter: false,
            userHasDepartmentsOrTeams: true,
            departments: [],
            teams: [],
            selectedDepartments: [],
            selectedTeams: [],
            members: [],
            timeout: null,
            limit: 100,
            offset: 0,
            canLoadMore: true,
            loading: true,
            pickerReady: false,
            filterDunningActive: false,
            filterDunningStopActive: false,
        };
    },
    computed: {
        createMemberLink() {
            return `/backend/clubs/${this.clubId}/members/create`;
        },
        makeSelectedDepartmentsParameter() {
            const departments = Array.from(this.selectedDepartments);
            if (departments.length === 0) {
                return '';
            }
            return this.getDepartmentIds(departments);
        },
        makeSelectedTeamsParameter() {
            return this.selectedTeams.map((element) => element.id).join(',');
        },
        filteredDepartments() {
            return this.departments.filter((department) => {
                return this.selectedDepartments.indexOf(department) === -1;
            });
        },
        filteredTeams() {
            return this.teams.filter((team) => {
                return this.selectedTeams.indexOf(team) === -1;
            });
        },
        dunningFilters() {
            let filter = '';
            if (this.filterDunningActive) {
                filter += '&filterDunningActive=1';
            }
            if (this.filterDunningStopActive) {
                filter += '&filterDunningStopActive=1';
            }
            return filter;
        },
    },
    watch: {
        filterDunningActive() {
            if (this.pickerReady) {
                this.load();
            }
        },
        filterDunningStopActive() {
            if (this.pickerReady) {
                this.load();
            }
        },
        search() {
            clearTimeout(this.timeout);
            if (this.search.trim().length < 3 && this.search.trim().length > 0) {
                return;
            }
            this.timeout = setTimeout(() => {
                if (this.pickerReady) {
                    this.load();
                }
            }, 600);
        },
    },
    created() {
        const view = localStorage.getItem(MEMBERS_OVERVIEW_VIEW);
        if (view !== null && view !== '') {
            this.view = view;
        }
        const statuses = JSON.parse(localStorage.getItem(MEMBERS_OVERVIEW_STATUSES));
        if (statuses !== null) {
            this.statuses = statuses;
        }
        const types = JSON.parse(localStorage.getItem(MEMBERS_OVERVIEW_TYPES));
        if (types !== null) {
            this.types = types;
        }

        this.sort = localStorage.getItem(MEMBERS_OVERVIEW_SORT);
        if (!this.sort) {
            this.sort = 'lastName';
        }
    },
    mounted() {
        this.initDepartmentAndTeams();
    },
    methods: {
        updateView(view) {
            this.view = view;

            if (view !== null) {
                localStorage.setItem(MEMBERS_OVERVIEW_VIEW, view);
            } else {
                localStorage.removeItem(MEMBERS_OVERVIEW_VIEW);
            }
        },
        updateSort(sort) {
            this.sort = sort;
            if (sort !== null) {
                localStorage.setItem(MEMBERS_OVERVIEW_SORT, sort);
            } else {
                localStorage.removeItem(MEMBERS_OVERVIEW_SORT);
            }
            this.load();
        },
        updateStatuses(statusesAndTypes) {
            this.statuses = statusesAndTypes.statuses;
            this.types = statusesAndTypes.types;

            if (this.statuses !== null) {
                localStorage.setItem(MEMBERS_OVERVIEW_STATUSES, JSON.stringify(this.statuses));
            } else {
                localStorage.removeItem(MEMBERS_OVERVIEW_STATUSES);
            }

            if (this.types !== null) {
                localStorage.setItem(MEMBERS_OVERVIEW_TYPES, JSON.stringify(this.types));
            } else {
                localStorage.removeItem(MEMBERS_OVERVIEW_TYPES);
            }
            this.load();
        },
        initDepartmentAndTeams() {
            ClubEmployeeDepartmentsAndTeamsRepository.findByContext(this.clubId, 'members')
                .then((clubEmployeeDepartmentsAndTeams) => {
                    this.departments = clubEmployeeDepartmentsAndTeams.departments;
                    this.teams = clubEmployeeDepartmentsAndTeams.teams;

                    if (this.isShortProfileGloballyViewable) {
                        if (!this.departments.reduce((previousValue, currentElement) => {
                            return previousValue || currentElement.isViewable;
                        }, false)
                        && !this.teams.reduce((previousValue, currentElement) => {
                            return previousValue || currentElement.isBasicDataEditable;
                        }, false)) {
                            this.userHasDepartmentsOrTeams = false;
                        }

                        this.departments.forEach((element) => element.makeEditable());
                        this.teams.forEach((element) => element.makeEditable());
                    }
                    if (!this.isShortProfileGloballyViewable && this.userHasDepartmentsOrTeams) {
                        this.selectedDepartments = Array.from(this.departments.filter((element) => element.isViewable));
                        this.departments.forEach((department) => {
                            department.childDepartments.forEach((childDepartment) => {
                                if (childDepartment.isViewable) {
                                    this.selectedDepartments.push(childDepartment);
                                } else {
                                    childDepartment.childDepartments.forEach((childDepartment2) => {
                                        if (childDepartment2.isViewable) {
                                            this.selectedDepartments.push(childDepartment2);
                                        }
                                    });
                                }
                            });
                        });
                        this.selectedTeams = Array.from(this.teams.filter((element) => element.isBasicDataEditable));
                    }
                    this.loading = false;
                    this.load();
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        load() {
            this.members = [];
            this.offset = 0;
            this.loading = true;
            if (this.selectedDepartments.length === 0 && this.selectedTeams.length === 0) {
                this.loadAll()
                    .then((members) => {
                        this.members = members;
                        this.pickerReady = true;
                    })
                    .catch(() => {
                        this.$notify.error('error.loadingData');
                    })
                    .finally(() => this.loading = false);
            } else if (this.selectedDepartments.length > 0 || this.selectedTeams.length > 0) {
                this.loadByDepartmentOrTeams()
                    .then((members) => {
                        this.members = members;
                        this.pickerReady = true;
                    })
                    .catch(() => {
                        this.$notify.error('error.loadingData');
                    })
                    .finally(() => this.loading = false);
            }
        },
        loadMore() {
            this.offset += this.limit;
            this.loading = true;
            if (this.selectedDepartments.length === 0 && this.selectedTeams.length === 0) {
                this.loadAll()
                    .then((members) => {
                        this.canLoadMore = members.length > 0;
                        this.members = this.members.concat(members);
                        this.pickerReady = true;
                    })
                    .catch(() => {
                        this.$notify.error('error.loadingData');
                    })
                    .finally(() => this.loading = false);
            } else if (this.selectedDepartments.length > 0 || this.selectedTeams.length > 0) {
                this.loadByDepartmentOrTeams()
                    .then((members) => {
                        this.canLoadMore = members.length > 0;
                        this.members = this.members.concat(members);
                        this.pickerReady = true;
                    })
                    .catch(() => {
                        this.$notify.error('error.loadingData');
                    })
                    .finally(() => this.loading = false);
            }
        },
        loadAll() {
            return MemberRepository.findAll(this.clubId, this.statuses.join(','), this.types.join(','), this.dunningFilters, this.search, this.sort, this.limit, this.offset);
        },
        loadByDepartmentOrTeams() {
            return MemberRepository.findByDepartmentAndTeamIds(this.clubId, this.statuses.join(','), this.types.join(','), this.dunningFilters, this.search, this.sort, this.limit, this.makeSelectedDepartmentsParameter, this.makeSelectedTeamsParameter, this.offset);
        },
        getDepartmentIds(departments) {
            if (departments[0].childDepartments.length > 0) {
                departments = departments.concat(departments[0].childDepartments);
                return `${departments[0].id},${this.getDepartmentIds(departments.slice(1))}`;
            }
            if (departments.length === 1) {
                return departments[0].id;
            } else {
                return `${departments[0].id},${this.getDepartmentIds(departments.slice(1))}`;
            }
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
</style>
